import React, { useState, useEffect } from "react";
import { Form, Dropdown, Popup, Icon } from "semantic-ui-react";
import CaseType from "../../../models/CaseType";
import CaseCategory from "../../../models/CaseCategory";
import CaseTypeRepository from "../../../common/repository/CaseTypeRepository";
import CaseCategoryRepository from "../../../common/repository/CaseCategoryRepository";
import ClientRepository from "../../../common/repository/ClientRepository";
import Result from "../../../common/repository/Result";
import FormLabel from "../../common/FormLabel";

interface Props {
  value: CaseFormInputTypeAndCategoryValue;
  onChange(value: CaseFormInputTypeAndCategoryValue): void;
  onCaseTypeChange?(value: CaseType): void;
  onCaseCategoryChange?(value: CaseCategory): void;
  labelWidth?: string;
  clientId?: string;
  clientRepo?: ClientRepository;
}

export interface CaseFormInputTypeAndCategoryValue {
  caseCategory: string;
  caseType: string;
}

const CaseFormInputTypeAndCategory = (props: Props) => {
  const caseTypeRepo = new CaseTypeRepository();
  const caseCategoryRepo = new CaseCategoryRepository();

  const {
    value,
    onChange,
    onCaseTypeChange,
    onCaseCategoryChange,
    labelWidth,
    clientId,
    clientRepo,
  } = props;

  /** states for component */
  const [loadingCaseTypes, setLoadingCaseTypes] = useState(false);
  const [loadingCaseCategories, setLoadingCaseCategories] = useState(false);
  const [caseTypes, setCaseTypes] = useState<CaseType[]>([]);
  const [caseCategories, setCaseCategories] = useState<CaseCategory[]>([]);

  /** effects */
  useEffect(() => {
    setLoadingCaseTypes(true);
    (async () => {
      let result;
      if (clientId && clientRepo) {
        // Fetch client to get its case types
        const clientResult = await clientRepo.getClients({ id: clientId });
        if (clientResult instanceof Result.Success && clientResult.data.items.length > 0) {
          const client = clientResult.data.items[0];
          const clientCaseTypes = client.getCaseTypes();
          // If client has case types, use them; otherwise fallback to all case types
          if (clientCaseTypes.length > 0) {
            setCaseTypes(clientCaseTypes);
            // Clear selected case type if it's not in the new list
            if (value.caseType) {
              const isCaseTypeInList = clientCaseTypes.some(ct => ct.getId() === value.caseType);
              if (!isCaseTypeInList) {
                onChange({ caseType: "", caseCategory: "" });
              }
            }
            setLoadingCaseTypes(false);
            return;
          }
        }
      }
      // Fallback to all case types if no client selected or client has no case types
      result = await caseTypeRepo.getCaseTypes();
      if (result instanceof Result.Success) {
        setCaseTypes(result.data.items);
        // Clear selected case type if it's not in the new list
        if (value.caseType && clientId) {
          const isCaseTypeInList = result.data.items.some(ct => ct.getId() === value.caseType);
          if (!isCaseTypeInList) {
            onChange({ caseType: "", caseCategory: "" });
          }
        }
      }
      setLoadingCaseTypes(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    setLoadingCaseCategories(true);
    (async () => {
      const result = await caseCategoryRepo.getCategories({isDeleted: false});
      if (result instanceof Result.Success)
        setCaseCategories(result.data.items);
      setLoadingCaseCategories(false);
    })();
  }, []);

  /** helper functions */
  const caseTypesToOptions = () =>
    caseTypes.map((it) => {
      return { text: it.getTitle(), value: it.getId(), key: it.getId() };
    });
  const caseCategotriesToOptions = () => {
    // Include all categories, but prioritize non-activated ones
    // Also include the currently selected category if it exists
    const currentCategoryCode = internalValue.caseCategory;
    return caseCategories
      .filter((it) => {
        // Include if not activated, or if it's the currently selected category
        return it.getActivated() === false || it.getCode() === currentCategoryCode;
      })
      .map((it) => {
        return { text: it.getTitle(), value: it.getCode(), key: it.getId() };
      });
  };
  const findCaseTypeById = (id: string) =>
    caseTypes.find((it) => it.getId() === id);
  const findCaseCategoryByCode = (code: string) =>
    caseCategories.find((it) => it.getCode() === code);

  // Sync internalValue with prop value
  const [internalValue, setInternalValue] = useState(value);
  
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const internalOnChange = <T extends keyof CaseFormInputTypeAndCategoryValue>(
    key: T,
    val: string,
    accumulate: boolean = false
  ) => {
    if (accumulate) {
      const newValue = { ...internalValue, [key]: val };
      setInternalValue(newValue);
      onChange(newValue);
      return;
    }
    const newValue = { ...internalValue, [key]: val };
    setInternalValue(newValue);
    onChange(newValue);
  };

  const width = labelWidth || "100px";
  return (
    <>
      {/* case type input */}
      <Form.Group inline>
        <Form.Field required>
          <FormLabel width={width}>Case Type</FormLabel>
          <Dropdown
            search
            selection
            placeholder="Select Case Type"
            loading={loadingCaseTypes}
            value={internalValue.caseType}
            options={caseTypesToOptions()}
            onChange={(_, { value }) => {
              const caseType = findCaseTypeById(value as string);
              if (caseType === undefined) return;

              const caseCategoryCode = caseType.getCaseCategoryCode();
              
              // Create new value with updated case type
              const newValue: CaseFormInputTypeAndCategoryValue = { 
                ...internalValue, 
                caseType: value as string,
                caseCategory: "" // Initialize as empty, will be set below if found
              };
              
              // Find and set the case category based on the selected case type
              if (caseCategoryCode && caseCategoryCode.trim().length > 0) {
                // Find case category by code (check all categories, not just filtered ones)
                const caseCategory = caseCategories.find((it) => it.getCode() === caseCategoryCode);
                
                if (caseCategory !== undefined) {
                  // Set the case category code
                  newValue.caseCategory = caseCategory.getCode();
                  if (onCaseCategoryChange) onCaseCategoryChange(caseCategory);
                } else {
                  // Category not found in list - still set the code if we have it
                  newValue.caseCategory = caseCategoryCode;
                }
              }

              // Update both values at once
              setInternalValue(newValue);
              onChange(newValue);
              
              if (onCaseTypeChange) onCaseTypeChange(caseType);
            }}
          />
        </Form.Field>
      </Form.Group>
      {/* case type input */}

      {/* case category input */}
      <Form.Group inline>
        <Form.Field required>
          <FormLabel width={width}>Case Category</FormLabel>
          <Dropdown
            disabled
            selection
            placeholder="Select Category"
            loading={loadingCaseCategories}
            value={internalValue.caseCategory}
            options={caseCategotriesToOptions()}
            onChange={(_, { value }) => {
              internalOnChange("caseCategory", value as string);
              const caseCategory = findCaseCategoryByCode(value as string);
              if (caseCategory === undefined) return;

              if (onCaseCategoryChange) onCaseCategoryChange(caseCategory);
            }}
          />
        </Form.Field>
        <Form.Field>
          <Popup
            position="top center"
            inverted
            trigger={<Icon name="warning sign" color="orange" />}
          >
            <p>Case Category is automatically set from Case Type</p>
          </Popup>
        </Form.Field>
      </Form.Group>
      {/* case category input */}
    </>
  );
};

/** initial value to be used when not provided any  */
CaseFormInputTypeAndCategory.initialValue = {
  caseType: "",
  caseCategory: "",
} as CaseFormInputTypeAndCategoryValue;

export default CaseFormInputTypeAndCategory;
